<script lang="ts" setup>
import { createReusableTemplate } from '@vueuse/core'

type SeoImageType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif' | 'image/avif'

interface SeoMeta {
  title?: string
  description?: string
  image?: string
  imageType?: SeoImageType
}

interface PageHeaderMeta {
  description?: string
  title?: string
  icon?: string
}

interface Props {
  title?: string
  seo?: SeoMeta
  cta?: string
  ctaBtnText?: string
  contact?: boolean
  pageHeader?: PageHeaderMeta
}

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<Props>()

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const { appFrontEndDomain } = runtimeConfig.public
const expanded = useNavExpanded()

expanded.value = false

const pageTitle = computed(() => (route.meta.title as string | undefined) || props.title)
const pageHeader = computed(() => (route.meta as { pageHeader?: PageHeaderMeta }).pageHeader ?? props.pageHeader)

const defaultSeo = {
  title: 'Forgd: A new kind of Web3 investment bank',
  description: 'Forgd provides blockchain projects with free tools and solutions for token launch and protocol optimization—all in a single platform.',
}

const seoTitle = computed(() => props.seo?.title || defaultSeo.title)
const seoDescription = computed(() => props.seo?.description || defaultSeo.description)
const defaultSeoImage = 'https://cdn.prod.website-files.com/6642268ef69d551ef8d67770/66d72c1ba054de8cd491c239_opengraph-forgd.jpg'
const seoImage = computed(() => props.seo?.image || defaultSeoImage)
const seoImageType = computed<SeoImageType>(() => props.seo?.imageType || 'image/jpeg')
const { isContentSearchModalOpen } = useUIState()

useHead({
  title: pageTitle,
  htmlAttrs: {
    class: 'scroll-smooth',
  },
})

useSeoMeta({
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogImage: seoImage,
  ogImageType: seoImageType,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogType: 'website',
  ogSiteName: 'Forgd',
  ogUrl: () => `${appFrontEndDomain}${route.path}`,
  twitterCard: 'summary_large_image',
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: seoImage,
})

const [DefineFooter, ReuseFooter] = createReusableTemplate()

const searchTerm = ref('')
const { searchGroups } = useAppSearch(searchTerm)

onMounted(() => {
  const getScrollOffset = () =>
    window.matchMedia('(max-width: 767px)').matches ? 104 : 40

  const scrollToHash = (hash: string) => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''))
      if (element) {
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - getScrollOffset()

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      }
    }
  }

  watch(() => route.hash, scrollToHash, { immediate: true })
})
</script>

<template>
  <DefineFooter>
    <slot name="footer" />
  </DefineFooter>
  <div
    data-layout="public"
    class="flex min-h-screen flex-col bg-neutral-200 font-display font-normal text-primal"
  >
    <div class="flex min-w-0 grow">
      <AppNav />
      <div class="w-full min-w-0">
        <div class="relative flex">
          <main class="flex min-w-0 grow flex-col" data-inertable>
            <CorePageHeaderMobile />
            <div
              class="relative hidden h-20 items-center justify-between border-b border-default px-5 md:flex"
            >
              <slot name="header">
                <CorePageHeader
                  :title="pageHeader?.title"
                  :icon="pageHeader?.icon"
                  :description="pageHeader?.description"
                >
                  <template v-if="$slots['header-prefix']" #title-prefix>
                    <slot name="header-prefix" />
                  </template>
                  <template v-if="$slots['header-suffix']" #title-suffix>
                    <slot name="header-suffix" />
                  </template>
                </CorePageHeader>
                <ClientOnly>
                  <LazyCoreSearchModal v-model="searchTerm" v-model:is-open="isContentSearchModalOpen" :groups="searchGroups" />
                </ClientOnly>
              </slot>
            </div>

            <div class="flex min-h-[calc(100vh-80px)] w-full flex-col md:flex-row">
              <slot v-if="$slots.sidebar" name="sidebar" />
              <div class="w-full min-w-0 grow">
                <slot name="subheader" />
                <div class="flex grow flex-col space-y-10" :class="route.meta.noPadding ? '' : 'mx-auto mb-10 max-w-300'">
                  <slot />
                </div>
                <CoreSupportCta v-if="contact" :cta="cta" :cta-btn-text="ctaBtnText" />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>

    <footer class="w-full min-w-0 overflow-x-clip">
      <ReuseFooter />
      <slot v-if="$slots['app-footer']" name="app-footer" />
      <CoreFooter />
    </footer>

    <AuthLoginModal />
  </div>
</template>
